import { useEffect, useRef } from 'react';

import { useParams } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import {
  Box,
  Button,
  Flex,
  useToast,
  Link,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { apiCreateBinderPDF, apiGetBinderPDF } from 'api';

import { setBinderPDF as setAutoBinderPDF } from 'reducers/autoPersonalFormReducer';
import { setBinderPDF as setHomeBinderPDF } from 'reducers/homePersonalFormReducer';
import { setBinderPDF as setCommercialBinderPDF } from 'reducers/commercialFormReducer';

import type { APIBinderPDF } from 'types/api.types';
import type { RootState } from 'store';

import {
  BINDER_STATUS_GENERATING,
  BINDER_STATUS_COMPLETED,
  BINDER_STATUS_ERROR,
} from 'variables/constants';

import StepContainer from './StepContainer';
import StepHeader from './StepHeader';
import StatusBadge from './StatusBadge';
import type { BinderFormStateKey } from './stepTypes';

interface StepOneProps {
  formStateKey: BinderFormStateKey;
}

const ACTIONS = {
  autoPersonalForm: setAutoBinderPDF,
  homePersonalForm: setHomeBinderPDF,
  commercialForm: setCommercialBinderPDF,
};

const StepOne = ({ formStateKey }: StepOneProps) => {
  const { uuid } = useParams();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const setBinderPDF = ACTIONS[formStateKey];

  const binderPdf = useAppSelector(
    (state: RootState) => state[formStateKey].binder_pdf,
  );
  const signing = useAppSelector(
    (state: RootState) => state[formStateKey].signing,
  );

  const completeFormMutation = useMutation({
    mutationFn: () => {
      return apiCreateBinderPDF(uuid);
    },
    onSuccess: (binder: APIBinderPDF) => {
      dispatch(setBinderPDF(binder));

      toast({
        title: 'Binder generation started',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error generating binder',
        description: error?.error || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    },
  });

  const fetchBinderMutation = useMutation({
    mutationFn: (binderId: number) => {
      return apiGetBinderPDF(uuid, binderId);
    },
    onSuccess: (binder: APIBinderPDF) => {
      dispatch(setBinderPDF(binder));

      if (binder.status === BINDER_STATUS_COMPLETED) {
        toast({
          title: 'Binder generated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      } else if (binder.status === BINDER_STATUS_ERROR) {
        toast({
          title: 'Binder generation failed',
          description: 'There was an error generating the binder.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    },
    onError: (error) => {
      console.error('Failed to fetch binder status:', error);
    },
  });

  useEffect(() => {
    if (
      binderPdf &&
      binderPdf.status === BINDER_STATUS_GENERATING &&
      binderPdf.id
    ) {
      pollingIntervalRef.current = setInterval(() => {
        fetchBinderMutation.mutate(binderPdf.id);
      }, 5000);
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [binderPdf?.status, binderPdf?.id]);

  const getButtonText = () => {
    if (!binderPdf) return 'Generate Binder';
    if (binderPdf.status === BINDER_STATUS_COMPLETED)
      return 'Re-generate Binder';
    if (binderPdf.status === BINDER_STATUS_ERROR) return 'Generate Binder';
    return 'Generate Binder';
  };

  const getStatusContent = () => {
    if (!binderPdf) {
      return null;
    }

    if (binderPdf.status === BINDER_STATUS_GENERATING) {
      return (
        <Flex align="center" gap="10px" mb="15px">
          <Spinner size="sm" color="brand.500" />
          <Text fontSize="sm" color="gray.600">
            Generating binder...
          </Text>
        </Flex>
      );
    }

    if (binderPdf.status === BINDER_STATUS_COMPLETED && binderPdf.file) {
      return (
        <Box mb="15px">
          <Link
            href={binderPdf.file}
            target="_blank"
            rel="noopener noreferrer"
            color="brand.500"
            textDecoration="underline"
            fontSize="sm"
            fontWeight="semibold"
          >
            Download Binder PDF
          </Link>
        </Box>
      );
    }

    if (binderPdf.status === BINDER_STATUS_ERROR) {
      return (
        <Alert status="error" size="sm" mb="15px">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Generation Failed</AlertTitle>
            <AlertDescription fontSize="xs">
              There was an error generating the binder.
            </AlertDescription>
          </Box>
        </Alert>
      );
    }

    return null;
  };

  const isButtonDisabled = () => {
    return (
      completeFormMutation.isPending ||
      binderPdf?.status === BINDER_STATUS_GENERATING
    );
  };

  return (
    <StepContainer isEnabled={true}>
      <StepHeader stepNumber={1} title="Generate Binder PDF" isEnabled={true} />

      {getStatusContent()}

      {binderPdf && signing ? (
        <StatusBadge status="success">Binder sent for signing</StatusBadge>
      ) : (
        <Button
          onClick={() => completeFormMutation.mutate()}
          disabled={isButtonDisabled()}
          isLoading={completeFormMutation.isPending}
          variant="brand"
          size="sm"
          w="100%"
        >
          {getButtonText()}
        </Button>
      )}
    </StepContainer>
  );
};

export default StepOne;
